import { useTheme } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPLabel, IPSeparator, IPSpinButton } from 'baseComponents';
import { IPDropdown } from 'baseComponents/Dropdown/IPDropdown';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { IPTextField } from 'baseComponents/TextField/IPTextField';
import Accordion from 'components/Accordion/Accordion';
import { ExpressionValidationState } from 'components/KPI/Expression/ExpressionEditor';
import { getAttachmentCountOptions, getRequiredOptionalOptions } from 'components/KPI/KPIHelpers';
import KPITypeConfigRenderer from 'components/KPI/KPITypeConfigRenderer';
import KPITypePicker from 'components/KPI/KPITypePicker';
import TextInfo from 'components/Notification/Info';
import AuthSchemaPicker from 'components/Pickers/AuthSchemaPicker';
import KeyValueTagPicker from 'components/Pickers/KeyValueTagPicker';
import SelectListAndFolder from 'components/lists/SelectListAndFolder';
import { globalStackTokensGapMedium, globalStackTokensGapSmall } from 'globalStyles';
import { TFunction } from 'i18next';
import KPI, { KPITypes } from 'models/kpi/kpi';
import StringValue from 'models/stringValue/stringValue';
import Tag from 'models/tag';
import { ReactNode, RefObject, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { hasUserFeatureGenericManager } from 'services/Auth/featurePermissions';

export interface KPIDetailsFormProps {
  kpi: KPI;
  // All KPIs, used by the expression editor to offer/validate formula operands.
  kpis?: KPI[];
  formKpiIds?: number[];
  originalExpression?: string;
  expressionValidationUpdated?: (state: ExpressionValidationState) => void;
  mode: 'add' | 'edit';
  entityTerm: (isField: boolean) => string;
  onChange: (kpi: KPI) => void;
  restrictTypeChanges: boolean;
  onPickType: (type: KPITypes) => void;
  onIsFieldChange?: (isField: boolean) => void;
  defValueUpdated: (defValue: string | undefined) => void;
  minValueUpdated: (minValue: string | undefined) => void;
  maxValueUpdated: (maxValue: string | undefined) => void;
  requiredUpdated: (required: boolean) => void;
  choicesUpdated: (choices: StringValue[] | undefined, defvalue: string | undefined) => void;
  decimalCountUpdated: (decimalCount: number | undefined) => void;
  showLabelUpdated: (showLabel: boolean) => void;
  setList: (listId: number | undefined, folderId: string | undefined) => void;
  tags: Tag[];
  onAddTag: (tag: Tag) => void;
  onRemoveTag: (tag: Tag) => void;
  onCreateTag: (tag: Tag) => void;
  nameInfoMessage: string;
  nameErrorMessage?: string;
  nameFieldErrorBorder?: boolean;
  fieldError: (field: string) => string | undefined;
  onNameChange: (kpi: KPI) => void;
  nameFieldRef?: RefObject<HTMLDivElement>;
  showTypeConfig: boolean;
  showInstruction: boolean;
  showExpectedResult: boolean;
  showAttachments: boolean;
  showPermissions: boolean;
  initialAccordion?: 1 | 2 | 3;
  compactLayout?: boolean;
  renderAfterName?: () => ReactNode;
}

const KPIDetailsForm = (props: KPIDetailsFormProps) => {
  const appContext = useContext(AppContext);
  const { t } = useTranslation(['kpis', 'forms']);
  const theme = useTheme();
  const [acc1, setAcc1] = useState<boolean>(props.initialAccordion === 1);
  const [acc2, setAcc2] = useState<boolean>(props.initialAccordion === 2);
  const [acc3, setAcc3] = useState<boolean>(props.initialAccordion === 3);

  const onToggle = (nr: number, expanded: boolean) => {
    switch (nr) {
      case 1:
        setAcc1(expanded);
        setAcc2(false);
        setAcc3(false);
        break;
      case 2:
        setAcc1(false);
        setAcc2(expanded);
        setAcc3(false);
        break;
      case 3:
        setAcc1(false);
        setAcc2(false);
        setAcc3(expanded);
        break;
    }
  };

  const nameError =
    props.nameErrorMessage !== undefined ? props.nameErrorMessage : props.fieldError('name');

  return (
    <>
      <IPStack.Item>
        <TextInfo message={props.nameInfoMessage}>
          <IPLabel>{t('kpis:Fields.Name')}</IPLabel>
        </TextInfo>
        <div ref={props.nameFieldRef}>
          <IPTextField
            required
            value={props.kpi.name}
            placeholder={t('kpis:Fields.NamePlaceholder')}
            onChange={(ev, newValue) => {
              const newKPI = props.kpi.clone();
              newKPI.name = newValue?.trimStart() ?? '';
              props.onNameChange(newKPI);
            }}
            maxLength={512}
            errorMessage={nameError}
            styles={props.nameFieldErrorBorder ? { fieldGroup: { borderColor: theme.semanticColors.errorText } } : undefined}
          />
        </div>
        {props.renderAfterName?.()}
      </IPStack.Item>
      <IPStack.Item>
        <TextInfo message={t('kpis:Fields.TypeInfo', { entity: props.entityTerm(props.kpi.isField) })}>
          <IPLabel>{t('kpis:Fields.Type')}</IPLabel>
        </TextInfo>
        <KPITypePicker
          selectedType={props.kpi.type}
          restrictTypeChanges={props.restrictTypeChanges}
          compactLayout={props.compactLayout}
          onPickType={props.onPickType}
          onIsFieldChange={props.onIsFieldChange}
        />
      </IPStack.Item>
      <IPSeparator styles={{ root: { padding: 0, height: 1 } }} />
      {props.showTypeConfig && (
        <Accordion
          title={t('kpis:Fields.SectionType', { type: props.kpi.getTypeText(t as TFunction<string[]>) })}
          showBorder={true}
          active={acc1}
          onToggle={(expanded) => onToggle(1, expanded)}
        >
          <KPITypeConfigRenderer
            kpi={props.kpi}
            kpis={props.kpis}
            formKpiIds={props.formKpiIds}
            originalExpression={props.originalExpression}
            expressionValidationUpdated={props.expressionValidationUpdated}
            defValueUpdated={props.defValueUpdated}
            minValueUpdated={props.minValueUpdated}
            maxValueUpdated={props.maxValueUpdated}
            requiredUpdated={props.requiredUpdated}
            choicesUpdated={props.choicesUpdated}
            decimalCountUpdated={props.decimalCountUpdated}
            showLabelUpdated={props.showLabelUpdated}
          />
        </Accordion>
      )}
      <Accordion
        title={t('kpis:Fields.SectionTexts')}
        showBorder={true}
        active={acc2}
        onToggle={(expanded) => onToggle(2, expanded)}
      >
        <IPStack tokens={globalStackTokensGapMedium} styles={{ root: { padding: 10 } }}>
          <IPStack.Item>
            <TextInfo message={t('kpis:Fields.DescriptionInfo', { entity: props.entityTerm(props.kpi.isField) })}>
              <IPLabel>{t('kpis:Fields.Description')}</IPLabel>
            </TextInfo>
            <IPTextField
              value={props.kpi.description ?? ''}
              placeholder={t('kpis:Fields.DescriptionPlaceholder')}
              onChange={(ev, newValue) => {
                const newKPI = props.kpi.clone();
                newKPI.description = newValue ?? '';
                props.onChange(newKPI);
              }}
              maxLength={1024}
            />
          </IPStack.Item>
          {props.showInstruction && (
            <IPStack.Item>
              <TextInfo message={t('kpis:Fields.InstructionInfo', { entity: props.entityTerm(props.kpi.isField) })}>
                <IPLabel>{t('kpis:Fields.Instruction')}</IPLabel>
              </TextInfo>
              <IPTextField
                value={props.kpi.instruction ?? ''}
                placeholder={t('kpis:Fields.InstructionPlaceholder')}
                onChange={(ev, newValue) => {
                  const newKPI = props.kpi.clone();
                  newKPI.instruction = newValue ?? '';
                  props.onChange(newKPI);
                }}
                maxLength={1024}
              />
            </IPStack.Item>
          )}
          {props.showExpectedResult && (
            <IPStack.Item>
              <TextInfo message={t('kpis:Fields.ExpectedResultInfo', { entity: props.entityTerm(props.kpi.isField) })}>
                <IPLabel>{t('kpis:Fields.ExpectedResult')}</IPLabel>
              </TextInfo>
              <IPTextField
                value={props.kpi.expectedResult ?? ''}
                placeholder={t('kpis:Fields.ExpectedResultPlaceholder')}
                onChange={(ev, newValue) => {
                  const newKPI = props.kpi.clone();
                  newKPI.expectedResult = newValue ?? '';
                  props.onChange(newKPI);
                }}
                maxLength={1024}
              />
            </IPStack.Item>
          )}
          <IPStack.Item>
            <TextInfo message={t('kpis:Fields.TagsInfo', { entity: props.entityTerm(props.kpi.isField) })}>
              <IPLabel>{t('kpis:Fields.Tags')}</IPLabel>
            </TextInfo>
            <KeyValueTagPicker
              selectedTags={props.tags}
              onAdd={props.onAddTag}
              onRemove={props.onRemoveTag}
              onCreate={props.onCreateTag}
              allowCreate={hasUserFeatureGenericManager(appContext)}
            />
          </IPStack.Item>
        </IPStack>
      </Accordion>
      {props.showAttachments && (
        <Accordion
          title={t('kpis:Fields.Attachments')}
          showBorder={true}
          active={acc3}
          onToggle={(expanded) => onToggle(3, expanded)}
        >
          <IPStack tokens={globalStackTokensGapMedium} styles={{ root: { padding: 10 } }}>
            <IPStack.Item>
              <IPStack horizontal wrap tokens={globalStackTokensGapMedium}>
                <IPStack>
                  <TextInfo message={t('kpis:Fields.AttachmentsInfo')}>
                    <IPLabel>{t('kpis:Fields.Attachments')}</IPLabel>
                  </TextInfo>
                  <IPStack horizontal tokens={globalStackTokensGapSmall}>
                    <IPStack.Item>
                      <IPDropdown
                        options={getRequiredOptionalOptions(t as TFunction<string[]>)}
                        selectedKey={Math.sign(props.kpi.attachmentMode ?? 0)}
                        onChange={(ev, option) => {
                          const newKPI = props.kpi.clone();
                          newKPI.attachmentMode = option?.key as number;
                          props.onChange(newKPI);
                        }}
                        styles={{ root: { width: 100 } }}
                      />
                    </IPStack.Item>
                    <IPStack.Item>
                      <IPDropdown
                        disabled={props.kpi.attachmentMode === 0}
                        options={getAttachmentCountOptions(t as TFunction<string[]>)}
                        selectedKey={Math.abs(props.kpi.attachmentMode ?? 1)}
                        onChange={(ev, option) => {
                          const nr = (option?.key as number) ?? 1;
                          const newKPI = props.kpi.clone();
                          newKPI.attachmentMode = Math.sign(props.kpi.attachmentMode ?? 1) * nr;
                          props.onChange(newKPI);
                        }}
                        styles={{ root: { width: 50 } }}
                      />
                    </IPStack.Item>
                  </IPStack>
                </IPStack>
                <IPStack>
                  <TextInfo message={t('kpis:Fields.CommentsInfo')}>
                    <IPLabel>{t('kpis:Fields.Comments')}</IPLabel>
                  </TextInfo>
                  <IPStack horizontal tokens={globalStackTokensGapSmall}>
                    <IPStack.Item>
                      <IPDropdown
                        options={getRequiredOptionalOptions(t as TFunction<string[]>)}
                        selectedKey={Math.sign(props.kpi.commentMode ?? 0)}
                        onChange={(ev, option) => {
                          const newKPI = props.kpi.clone();
                          newKPI.commentMode = (option?.key as number) * 100;
                          props.onChange(newKPI);
                        }}
                        styles={{ root: { width: 100 } }}
                      />
                    </IPStack.Item>
                    <IPStack.Item>
                      <IPSpinButton
                        disabled={props.kpi.commentMode === 0}
                        value={Math.abs(props.kpi.commentMode ?? 100).toString()}
                        min={2}
                        max={4000}
                        onChange={(ev, value) => {
                          const newKPI = props.kpi.clone();
                          newKPI.commentMode = Math.sign(props.kpi.commentMode ?? 100) * Number.parseInt(value ?? '');
                          props.onChange(newKPI);
                        }}
                        styles={{ root: { width: 50 } }}
                      />
                    </IPStack.Item>
                  </IPStack>
                </IPStack>
              </IPStack>
            </IPStack.Item>
            <IPStack.Item>
              <SelectListAndFolder
                listId={props.kpi.listId}
                folderId={props.kpi.folderId}
                onUpdate={props.setList}
                allowNavigation={true}
              />
            </IPStack.Item>
          </IPStack>
        </Accordion>
      )}
      {props.showPermissions && (
        <>
          <IPSeparator styles={{ root: { padding: 0, height: 1 } }} />
          <IPStack.Item>
            <AuthSchemaPicker
              selectedAuthSchemaId={props.kpi.authSchemaId}
              onSelect={(schema) => {
                const newKPI = props.kpi.clone();
                newKPI.authSchemaId = schema?.authSchemaId;
                props.onChange(newKPI);
              }}
            />
          </IPStack.Item>
        </>
      )}
    </>
  );
};

export default KPIDetailsForm;
